import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { Collection } from "../../../api/derin";
import { collectionColors, font, subtitleSx } from "./tokens";

interface AboutStudyModalProps {
  open: boolean;
  collection: Collection | null;
  onClose: () => void;
  onSubmit: (file: File) => void;
  isSubmitting?: boolean;
}

/**
 * The "+ About the Study" action. A written overview of the study — goals, methodology,
 * panel, sample — that the client app renders as its own tab. Like the configuration
 * file it is not versioned, so uploading replaces the one currently applied.
 */
const AboutStudyModal: React.FC<AboutStudyModalProps> = ({
  open,
  collection,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [file, setFile] = useState<File | null>(null);

  const close = () => {
    setFile(null);
    onClose();
  };

  const currentFileName = collection?.aboutFileName ?? null;

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="About the Study"
      subtitle="Upload a clear, structured overview of the study, including its goals, methodology, and relevant context."
      confirmLabel={currentFileName ? "Replace file" : "Upload"}
      onConfirm={() => {
        if (file) {
          onSubmit(file);
        }
      }}
      confirmDisabled={!file}
      isSubmitting={isSubmitting}
    >
      {collection && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px",
            background: collectionColors.surfaceLight,
            borderRadius: "8px",
          }}
        >
          <DescriptionOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
          <Box>
            <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
              Currently applied
            </Typography>
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: currentFileName ? collectionColors.textPrimary : collectionColors.textSecondary,
              }}
            >
              {currentFileName ?? "No About the Study file yet"}
            </Typography>
          </Box>
        </Box>
      )}

      <FileDropzone
        file={file}
        onSelect={setFile}
        placeholder="Upload About the Study .yaml / .yml"
      />
    </ModalShell>
  );
};

export default AboutStudyModal;
